import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, ArrowUpRight, MapPin, Clock, ArrowRight, Check, MessageSquare, ShieldCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { StarRating } from "../components/StarRating";
import VerifiedBadge from "../components/VerifiedBadge";
import TrustBadges from "../components/TrustBadges";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import FavoriteButton from "../components/FavoriteButton";
import ReportBusinessModal from "../components/ReportBusinessModal";
import SafeImage from "../components/SafeImage";
import BookingSheet from "../components/BookingSheet";

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const CANCELLATION_LABELS = {
  flexible: { label: "Flexible", desc: "Free cancellation up to 24 hours before the appointment." },
  moderate: { label: "Moderate", desc: "Free cancellation up to 48 hours before; a 50% fee applies after." },
  strict: { label: "Strict", desc: "Free cancellation up to 72 hours before; full deposit forfeit after." },
};

const fmtTime = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const d = new Date(); d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: m ? "2-digit" : undefined });
};

// Weekly open hours = union of every published service's availability windows.
function weeklyHours(services) {
  const byDay = {};
  (services || []).forEach((s) => {
    (s.availability || []).forEach((a) => {
      const d = Number(a.day);
      if (Number.isNaN(d) || !a.start || !a.end) return;
      if (!byDay[d]) byDay[d] = { start: a.start, end: a.end };
      else {
        if (a.start < byDay[d].start) byDay[d].start = a.start;
        if (a.end > byDay[d].end) byDay[d].end = a.end;
      }
    });
  });
  if (Object.keys(byDay).length === 0) return null;
  return DAY_LABELS.map((label, i) => ({
    label,
    hours: byDay[i] ? `${fmtTime(byDay[i].start)} – ${fmtTime(byDay[i].end)}` : null,
  }));
}

function normalizeUrl(u) {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  return "https://" + u;
}

const depositDollar = (s) => {
  const requires = s.requires_deposit !== false && Number(s.downpayment_amount || 0) > 0;
  if (!requires) return null;
  return s.deposit_type === "percent"
    ? +(Number(s.price || 0) * Number(s.downpayment_amount || 0) / 100).toFixed(2)
    : +Number(s.downpayment_amount || 0).toFixed(2);
};

function SubNav({ sections }) {
  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  return (
    <div className="sticky top-[61px] z-30 -mx-5 sm:mx-0 px-5 sm:px-0 backdrop-blur-xl border-b" style={{ background: "var(--gf-nav-bg)", borderColor: "var(--gf-border)" }} data-testid="storefront-subnav">
      <div className="max-w-5xl mx-auto flex gap-6 overflow-x-auto scrollbar-hide">
        {sections.map((s) => (
          <button key={s.id} onClick={() => go(s.id)} className="py-3.5 text-sm font-medium whitespace-nowrap hover:opacity-100 opacity-70 transition-opacity" style={{ color: "var(--gf-text)" }}>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReviewSummary({ reviews, avg }) {
  const count = reviews.length;
  const dist = useMemo(() => {
    const d = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((r) => { const k = Math.round(r.rating); if (d[k] != null) d[k]++; });
    return d;
  }, [reviews]);
  if (!count) return null;
  return (
    <div className="grid sm:grid-cols-[auto_1fr] gap-8 sm:gap-12 items-center max-w-xl">
      <div className="text-center sm:text-left">
        <div className="font-display text-5xl font-extrabold" style={{ color: "var(--gf-text)" }}>{avg.toFixed(1)}</div>
        <div className="mt-1"><StarRating value={avg} count={0} size={15} /></div>
        <div className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>{count} {count === 1 ? "review" : "reviews"}</div>
      </div>
      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((star) => {
          const pct = count ? (dist[star] / count) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-3 text-xs" style={{ color: "var(--gf-text-muted)" }}>
              <span className="w-3 text-right">{star}</span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--gf-surface-2)" }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--gf-text)" }} />
              </div>
              <span className="w-6 text-right tabular-nums">{dist[star]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BusinessProfile() {
  const { id } = useParams();
  const [biz, setBiz] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [bookingService, setBookingService] = useState(null);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const { user, authHeader } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const looksLikeHandle = typeof id === "string" && id.startsWith("@");
    const url = looksLikeHandle
      ? `${API}/businesses/by-handle/${encodeURIComponent(id.slice(1))}`
      : `${API}/businesses/${id}`;
    axios.get(url).then((r) => setBiz(r.data)).catch(() => setBiz(false));
  }, [id]);

  // LocalBusiness structured data for rich results on the storefront.
  useEffect(() => {
    if (!biz || biz === false) return;
    const reviews = biz.reviews || [];
    const avg = reviews.length ? reviews.reduce((a, r) => a + r.rating, 0) / reviews.length : 0;
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: biz.business_name,
      url: `https://bookfreely.co/business/${biz.business_handle ? "@" + biz.business_handle : biz.id}`,
      image: (biz.gallery_images || [])[0]?.url || biz.profile_image_url || undefined,
      description: biz.business_description || undefined,
      address: (biz.public_city || biz.public_state) ? {
        "@type": "PostalAddress",
        streetAddress: biz.public_address || undefined,
        addressLocality: biz.public_city || undefined,
        addressRegion: biz.public_state || undefined,
      } : undefined,
      aggregateRating: reviews.length ? {
        "@type": "AggregateRating",
        ratingValue: avg.toFixed(1),
        reviewCount: biz.review_count || reviews.length,
      } : undefined,
      makesOffer: (biz.services || []).slice(0, 20).map((s) => ({
        "@type": "Offer",
        priceCurrency: "USD",
        price: Number(s.price || 0).toFixed(2),
        itemOffered: { "@type": "Service", name: s.title },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-bf-ld", "localbusiness");
    el.textContent = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => {
      const ex = document.head.querySelector('script[data-bf-ld="localbusiness"]');
      if (ex) ex.remove();
    };
  }, [biz]);

  if (biz === null) return <div className="App"><Header /><div className="max-w-5xl mx-auto p-12" style={{ color: "var(--gf-text-muted)" }}>Loading…</div></div>;
  if (biz === false) return <div className="App"><Header /><div className="max-w-5xl mx-auto p-12" style={{ color: "var(--gf-text)" }}>Business not found.</div></div>;

  const sendMessage = async () => {
    if (!user) { nav("/login?next=/business/" + id); return; }
    if (user.role !== "customer") { toast.error("Only customers can DM a business"); return; }
    if (msgText.trim().length < 1) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/messages`, { business_id: id, text: msgText }, { headers: authHeader() });
      toast.success("Message sent");
      setMsgText(""); setMsgOpen(false);
      nav(`/messages?thread=${r.data.thread_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const reviews = biz.reviews || [];
  const ratingDocs = reviews.slice(0, 8);
  const avgRating = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length) : 0;
  const gallery = Array.isArray(biz.gallery_images) ? biz.gallery_images : [];
  const coverSrc = gallery[0]?.url || biz.profile_image_url || null;
  const verified = (biz.trust_badges || []).includes("verified");
  const hours = weeklyHours(biz.services);
  const policyKeys = Array.from(new Set((biz.services || [])
    .map((s) => s.cancellation_policy)
    .filter((k) => k && CANCELLATION_LABELS[k])));
  const PHOTO_PREVIEW = 7;

  const locationLine = biz.provides_mobile_services && !biz.has_storefront
    ? `Mobile service${biz.service_radius_miles ? ` · within ${Math.round(biz.service_radius_miles)} mi` : ""}${biz.public_service_area ? ` · ${biz.public_service_area}` : (biz.public_city || biz.public_state) ? ` · ${[biz.public_city, biz.public_state].filter(Boolean).join(", ")}` : ""}`
    : biz.public_address
      ? `${biz.public_address}${biz.public_city ? ", " + biz.public_city : ""}${biz.public_state ? ", " + biz.public_state : ""}`
      : biz.public_service_area || [biz.public_city, biz.public_state].filter(Boolean).join(", ");

  const sections = [
    { id: "sf-about", label: "Overview" },
    { id: "sf-services", label: "Services" },
    ...(gallery.length ? [{ id: "sf-gallery", label: "Photos" }] : []),
    ...(ratingDocs.length ? [{ id: "sf-reviews", label: "Reviews" }] : []),
    ...(hours ? [{ id: "sf-hours", label: "Hours" }] : []),
    { id: "sf-contact", label: "Contact" },
  ];

  return (
    <div className="App" style={{ background: "var(--gf-bg)" }}>
      <Header />

      {/* ───────── COVER ───────── */}
      <div className="relative w-full h-52 sm:h-72 lg:h-80 overflow-hidden" data-testid="business-profile-hero" style={{ background: "var(--gf-surface)" }}>
        {coverSrc ? (
          <SafeImage src={coverSrc} alt={`${biz.business_name} cover`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: "linear-gradient(135deg, var(--gf-surface), var(--gf-surface-2))" }} />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,23,42,0.55), rgba(15,23,42,0.05) 55%)" }} />
      </div>

      {/* ───────── IDENTITY ───────── */}
      <div className="max-w-5xl mx-auto px-5 sm:px-6">
        <div className="relative -mt-12 sm:-mt-14 flex items-end gap-4 sm:gap-5 flex-wrap">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden shrink-0" style={{ border: "4px solid var(--gf-bg)", boxShadow: "var(--gf-shadow-ambient)", background: "var(--gf-surface-2)" }}>
            <SafeImage
              src={biz.profile_image_url}
              alt={biz.business_name}
              testId="profile-hero-image"
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full flex items-center justify-center font-display text-4xl"
                     style={{ color: "var(--gf-text-muted)", background: "var(--gf-surface-2)" }}>
                  {(biz.business_name || "?").charAt(0).toUpperCase()}
                </div>
              }
            />
          </div>
          <div className="flex-1 min-w-[220px] pb-1">
            {Array.isArray(biz.service_categories) && biz.service_categories.length > 0 && (
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="profile-category-eyebrow" style={{ color: "var(--gf-text-muted)" }}>
                {biz.service_categories.join(" · ")}
              </div>
            )}
            <div className="flex items-center gap-2.5 mt-1 flex-wrap">
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "var(--gf-text)" }}>{biz.business_name}</h1>
              {verified && <VerifiedBadge size="sm" />}
            </div>
            {biz.owner_display_name && (
              <div className="mt-0.5 text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="profile-owner-display">by {biz.owner_display_name}</div>
            )}
            <div className="mt-2 flex items-center gap-4 flex-wrap text-sm" style={{ color: "var(--gf-text-soft)" }}>
              <StarRating value={avgRating} count={biz.review_count || 0} size={15} />
              {locationLine && (
                <span className="inline-flex items-center gap-1" data-testid="profile-location"><MapPin size={14} /> {locationLine}
                  {biz.provides_mobile_services && biz.has_storefront && <span style={{ color: "var(--gf-text-muted)" }}> · storefront + mobile</span>}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pb-1">
            <FavoriteButton targetType="business" targetId={biz.id} variant="inline" />
            <button data-testid="open-message-btn" onClick={() => { const el = document.getElementById("sf-contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); setMsgOpen(true); }}
              className="rounded-full font-semibold px-5 py-2.5 text-sm inline-flex items-center gap-2 transition-colors"
              style={{ border: "1px solid var(--gf-border-strong)", color: "var(--gf-text)", background: "var(--gf-bg)" }}>
              <MessageSquare size={15} /> Message
            </button>
            <button onClick={() => { const el = document.getElementById("sf-services"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
              className="rounded-full font-semibold px-5 py-2.5 text-sm inline-flex items-center gap-2 active:scale-[0.98]"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Book <ArrowRight size={15} />
            </button>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 flex-wrap">
          {Array.isArray(biz.trust_badges) && biz.trust_badges.filter((b) => b !== "verified").length > 0 && (
            <TrustBadges badges={biz.trust_badges.filter((b) => b !== "verified")} size="sm" />
          )}
          {biz.offers_in_person && <span className="bf-chip">In-person</span>}
          {biz.offers_online && <span className="bf-chip">Online</span>}
          {(!user || user.role === "customer") && (
            <button type="button" data-testid="open-report-modal-btn"
              onClick={() => { if (!user) { nav(`/login?next=/business/${id}`); return; } setReportOpen(true); }}
              className="text-[11px] ml-auto hover:underline" style={{ color: "var(--gf-text-muted)" }}>
              {user ? "Report" : "Sign in to report"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-6"><SubNav sections={sections} /></div>

      <div className="max-w-5xl mx-auto px-5 sm:px-6 pb-20">
        {/* ───────── ABOUT ───────── */}
        <section id="sf-about" className="pt-10 scroll-mt-28">
          {biz.business_description ? (
            <p className="text-lg leading-relaxed max-w-3xl" style={{ color: "var(--gf-text-soft)" }}>{biz.business_description}</p>
          ) : (
            <p className="text-lg" style={{ color: "var(--gf-text-muted)" }}>Welcome to {biz.business_name}.</p>
          )}
        </section>

        {/* ───────── SERVICES (clean rows, not boxes) ───────── */}
        <section id="sf-services" className="pt-14 scroll-mt-28">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--gf-text)" }}>Services</h2>
            <div className="text-sm" style={{ color: "var(--gf-text-muted)" }}>{biz.services.length} {biz.services.length === 1 ? "service" : "services"}</div>
          </div>

          {biz.services.length === 0 ? (
            <p className="mt-6 text-sm" style={{ color: "var(--gf-text-muted)" }}>This business hasn't published any services yet.</p>
          ) : (
            <div className="mt-6 divide-y" style={{ borderColor: "var(--gf-border)" }} data-testid="profile-services-grid">
              {biz.services.map((s) => {
                const dep = depositDollar(s);
                return (
                  <button key={s.id} type="button" onClick={() => setBookingService(s)} data-testid={`service-card-${s.id}`}
                    className="group w-full text-left flex items-center gap-4 sm:gap-5 py-5 first:pt-0" style={{ borderColor: "var(--gf-border)" }}>
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden shrink-0 bf-img-zoom-parent" style={{ background: "var(--gf-surface)" }}>
                      <SafeImage src={s.image_url} alt={s.title} className="w-full h-full object-cover bf-img-zoom"
                        fallback={<div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "var(--gf-text-muted)" }}>No photo</div>} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-bold leading-tight truncate" style={{ color: "var(--gf-text)" }}>{s.title}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs flex-wrap" style={{ color: "var(--gf-text-muted)" }}>
                        <span className="inline-flex items-center gap-1"><Clock size={12} /> {s.duration_minutes} min</span>
                        <span>· {s.category}</span>
                        {dep != null && <span>· {s.downpayment_refundable ? "Refundable" : "Non-refundable"} deposit ${dep.toFixed(2)}</span>}
                      </div>
                      {s.description && <p className="mt-1.5 text-sm line-clamp-1 hidden sm:block" style={{ color: "var(--gf-text-muted)" }}>{s.description}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-xl font-bold" style={{ color: "var(--gf-text)" }}>${Number(s.price || 0).toFixed(2)}</div>
                      <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: "var(--gf-accent)" }}>
                        Book <ArrowRight size={14} />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* ───────── GALLERY ───────── */}
        {gallery.length > 0 && (
          <section id="sf-gallery" className="pt-14 scroll-mt-28" data-testid="profile-gallery">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--gf-text)" }}>Photos</h2>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 auto-rows-[120px] sm:auto-rows-[150px] gap-3" data-testid="gallery-grid">
              {(() => {
                const limit = PHOTO_PREVIEW;
                const show = showAllPhotos ? gallery : gallery.slice(0, limit);
                const extra = gallery.length - limit;
                return show.map((g, i) => {
                  const isMain = i === 0;
                  const isLastPreview = !showAllPhotos && i === limit - 1 && extra > 0;
                  return (
                    <button key={g.id || i} type="button" onClick={() => { if (isLastPreview) setShowAllPhotos(true); }}
                      className={`relative rounded-2xl overflow-hidden bf-img-zoom-parent ${isMain ? "col-span-2 row-span-2" : ""}`}
                      style={{ background: "var(--gf-surface)", cursor: isLastPreview ? "pointer" : "default" }}>
                      <SafeImage src={g.url} alt={`${biz.business_name} photo ${i + 1}`} testId={`gallery-image-${i}`} className="w-full h-full object-cover bf-img-zoom" />
                      {isLastPreview && (
                        <div data-testid="gallery-more-overlay" className="absolute inset-0 flex items-center justify-center font-display text-base sm:text-lg font-bold" style={{ background: "rgba(15,23,42,0.62)", color: "#fff" }}>
                          +{extra} photos
                        </div>
                      )}
                    </button>
                  );
                });
              })()}
            </div>
          </section>
        )}

        {/* ───────── REVIEWS ───────── */}
        {ratingDocs.length > 0 && (
          <section id="sf-reviews" className="pt-14 scroll-mt-28">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--gf-text)" }}>Reviews</h2>
            <div className="mt-6"><ReviewSummary reviews={reviews} avg={avgRating} /></div>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-7" data-testid="profile-reviews">
              {ratingDocs.map((r) => (
                <div key={r.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--gf-text)" }}>
                      {r.customer_name}
                      <Check size={13} strokeWidth={3} style={{ color: "var(--gf-success)" }} aria-label="Booked on BookFreely" />
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <StarRating value={r.rating} count={0} size={13} />
                    {r.created_at && (
                      <span className="text-xs" data-testid="review-date" style={{ color: "var(--gf-text-muted)" }}>
                        {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    )}
                  </div>
                  {r.comment && <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--gf-text-soft)" }}>{r.comment}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ───────── HOURS ───────── */}
        {hours && (
          <section id="sf-hours" className="pt-14 scroll-mt-28" data-testid="profile-hours">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--gf-text)" }}>Hours</h2>
            <div className="mt-5 max-w-md divide-y rounded-2xl border" style={{ borderColor: "var(--gf-border)" }}>
              {hours.map((h) => (
                <div key={h.label} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`hours-row-${h.label.toLowerCase()}`}>
                  <span style={{ color: "var(--gf-text)" }}>{h.label}</span>
                  <span style={{ color: h.hours ? "var(--gf-text-soft)" : "var(--gf-text-muted)" }}>{h.hours || "Closed"}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs" style={{ color: "var(--gf-text-muted)" }}>Hours reflect published service availability. Exact open times are shown when you book.</p>
          </section>
        )}

        {/* ───────── POLICIES ───────── */}
        {policyKeys.length > 0 && (
          <section id="sf-policies" className="pt-14 scroll-mt-28" data-testid="profile-policies">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--gf-text)" }}>Policies</h2>
            <div className="mt-5 space-y-3 max-w-2xl">
              {policyKeys.map((k) => (
                <div key={k} className="flex items-start gap-3 rounded-2xl border p-4" style={{ borderColor: "var(--gf-border)" }} data-testid={`policy-${k}`}>
                  <ShieldCheck size={18} className="mt-0.5 shrink-0" style={{ color: "var(--gf-accent)" }} />
                  <div>
                    <div className="font-semibold text-sm" style={{ color: "var(--gf-text)" }}>{CANCELLATION_LABELS[k].label} cancellation</div>
                    <div className="text-sm mt-0.5" style={{ color: "var(--gf-text-soft)" }}>{CANCELLATION_LABELS[k].desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ───────── CONTACT ───────── */}
        <section id="sf-contact" className="pt-14 scroll-mt-28">
          <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--gf-text)" }}>Contact {biz.business_name}</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-8">
            <div data-testid="profile-contact-card">
              <ul className="space-y-2.5 text-sm">
                {biz.contact_phone && <li><a href={`tel:${biz.contact_phone}`} className="inline-flex items-center gap-2 hover:underline" style={{ color: "var(--gf-text)" }} data-testid="profile-phone"><Phone size={15} /> {biz.contact_phone}</a></li>}
                {biz.contact_email && <li><a href={`mailto:${biz.contact_email}`} className="inline-flex items-center gap-2 hover:underline" style={{ color: "var(--gf-text)" }} data-testid="profile-email"><Mail size={15} /> {biz.contact_email}</a></li>}
                {biz.website && <li><a href={normalizeUrl(biz.website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:underline" style={{ color: "var(--gf-text)" }} data-testid="profile-website"><ArrowUpRight size={15} /> {biz.website.replace(/^https?:\/\//, "")}</a></li>}
                {!biz.contact_phone && !biz.contact_email && (
                  <li data-testid="profile-contact-locked" style={{ color: "var(--gf-text-soft)" }}>
                    Phone and email are shared once a booking with this business is confirmed. Message them any time below.
                  </li>
                )}
              </ul>
            </div>
            <div>
              <p className="text-sm" style={{ color: "var(--gf-text-soft)" }}>Have a question? Message {biz.business_name} directly before you book.</p>
              {!msgOpen ? (
                <button onClick={() => setMsgOpen(true)} className="mt-3 rounded-full font-semibold px-5 py-2.5 text-sm inline-flex items-center gap-2"
                  style={{ border: "1px solid var(--gf-border-strong)", color: "var(--gf-text)", background: "var(--gf-bg)" }}>
                  <MessageSquare size={15} /> Message {biz.business_name}
                </button>
              ) : (
                <div className="mt-3" data-testid="message-composer">
                  <textarea data-testid="message-text" rows={3} value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder={`Ask ${biz.business_name} anything…`}
                    className="w-full rounded-xl px-3.5 py-3 outline-none text-sm" style={{ border: "1px solid var(--gf-border-strong)", background: "var(--gf-bg)", color: "var(--gf-text)" }} />
                  <div className="mt-2 flex gap-2">
                    <button data-testid="send-message-btn" disabled={sending || !msgText.trim()} onClick={sendMessage}
                      className="rounded-full font-semibold px-5 py-2 text-sm disabled:opacity-50" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                      {sending ? "Sending…" : "Send"}
                    </button>
                    <button onClick={() => setMsgOpen(false)} className="rounded-full px-4 py-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <Footer />
      {reportOpen && (
        <ReportBusinessModal businessId={biz.id} businessName={biz.business_name} onClose={() => setReportOpen(false)} />
      )}
      <BookingSheet service={bookingService} open={!!bookingService} onClose={() => setBookingService(null)} />
    </div>
  );
}
